import { takeLatest, put, select } from "redux-saga/effects"
import moment from "moment"

import { toast } from "react-toastify"

import {
  GET_USER_NOTIFICATIONS_SUCCESSFUL,
  COURSE_ENROLLMENT_SUCCESSFUL,
  COURSE_ENROLLMENT_FAILED,
  COURSE_BUNDLE_ENROLLMENT_SUCCESSFUL,
  COURSE_BUNDLE_ENROLLMENT_FAILED,
  GET_USER_PROFILE_SUCCESSFUL,
  GET_USER_PROFILE_FAILED,
  UPDATE_PROFILE_SUCCESS,
  START_COURSE_SUCCESSFUL,
  START_COURSE_FAILED,
  PATCH_NOTES_SUCCESSFUL,
  PATCH_NOTES_FAILED,
  COMPLETE_COURSEWARE_SUCCESSFUL,
  COMPLETE_COURSEWARE_FAILED,
  SET_COURSEWARE_PROGRESS_SUCCESSFUL,
  SET_COURSEWARE_PROGRESS_FAILED,
  UPDATE_PROFILE_FAILED,
  UPLOAD_KYC_ID_TEMPLATE_SUCCESSFUL,
  GET_CLASS_SCHEDULE_SUCCESSFUL,
  GET_CLASS_SCHEDULE_FAILED,
  START_EXAM_SUCCESSFUL,
  START_EXAM_FAILED,
  TOGGLE_JOIN_CLASS,
  SUBMIT_ANSWER_SUCCESSFUL,
  SUBMIT_ANSWER_FAILED,
  PAID_COURSE_ENROLLMENT_SUCCESSFUL,
  PAID_COURSE_ENROLLMENT_FAILED,
  UPDATE_MCLE_PROFILE_SUCCESS,
  GET_MCLE_PROFILE_SUCCESS,
  SUBMIT_MANUAL_KYC_SUCCESSFUL,
  PAID_BUNDLE_ENROLLMENT_SUCCESSFUL,
  PAID_BUNDLE_ENROLLMENT_FAILED,
  UPDATE_PROFILE_PHOTO_SUCCESSFUL,
  UPDATE_PROFILE_PHOTO_FAILED,
  VERIFY_WALLET_ADDRESS_SUCCESSFUL,
  VERIFY_WALLET_ADDRESS_FAILED,
  GET_USER_WALLET_NONCE_SUCCESSFUL,
  GET_USER_WALLET_NONCE_FAILED,
} from "./action-types"

import history from "app/history"

import {
  setUserNotifications,
  setUserProfile,
  showResponseModal,
  closeResponseModal,
  setUserError,
  getUserProfile,
  getCoursewareById,
  getMCLEProfile,
  setMCLEProfile,
  hideProfilePhotoModal,
  getWalletTransactions,
  getUserNotifications,
  getWalletBalance,
  setVerifyingAddress,
  setWalletConnected,
} from "store/user/actions"

import { logoutUser } from "store/auth/action"

import { setLMSTheme } from "store/courses/action"

import { MODAL_MESSAGES, ERROR_500 } from "constants/message"
import { CUSTOM_MODAL_TYPES, RESPONSE_MODAL_TYPES } from "constants/modal-types"

import { fetchCourseById, fetchBundleById } from "../courses/action"

import { isJsonString, isIdVerified } from "helpers/util"

function* watchGetUserNotifications() {
  yield takeLatest(
    GET_USER_NOTIFICATIONS_SUCCESSFUL,
    function* handleNotificationResponse({ payload }) {
      const { data } = payload
      let notification = yield select(
        (state) => state.userReducer.notifications
      )
      let newNotification = { ...data }
      if (data.meta.page > 1) {
        newNotification.data = notification.data.concat(data.data)
      }
      yield put(setUserNotifications(newNotification))
    }
  )
}

function* watchGetUserProfileSuccess() {
  yield takeLatest(
    GET_USER_PROFILE_SUCCESSFUL,
    function* handleGetUserProfile({ payload }) {
      const { data } = payload

      yield put(setUserProfile(data))
    }
  )
}

function* watchGetUserProfileFailed() {
  yield takeLatest(
    GET_USER_PROFILE_FAILED,
    function* handleGetUserProfileFailed({ error }) {
      const { status } = error.response || {}
      const token = yield localStorage.getItem("token")

      if ([401, 404].includes(status) && token) {
        //to do move this to axios interceptor
        yield localStorage.removeItem("userId")
        yield localStorage.removeItem("studentId")
        yield localStorage.removeItem("role")
        yield localStorage.removeItem("token")
        yield put(logoutUser())
        yield history.push("/")
        toast.error("Session expired, please log in to continue")
      }
    }
  )
}

function* watchCourseEnrollmentSuccess() {
  yield takeLatest(
    COURSE_ENROLLMENT_SUCCESSFUL,
    function* handleCourseEnrollmentSuccess({ payload }) {
      const {
        data,
        config: { data: requestPayload },
      } = payload
      const requestData = JSON.parse(requestPayload)
      let modalData = {
        modalType: RESPONSE_MODAL_TYPES.SUCCESS,
        header: data.success,
        message: MODAL_MESSAGES.ENROLLMENT_SUCCESSFUL,
        actionLabel: "START LEARNING",
        verified: true,
      }

      if (
        moment().isBefore(
          moment(requestData?.startDate, "MMMM DD, YYYY HH:mm a")
        )
      ) {
        const courses = yield select((state) => state.coursesReducer)
        const {
          selectedCourse: { id: courseId },
        } = courses
        modalData.message = `${MODAL_MESSAGES.ENROLLMENT_SUCCESSFUL_SPIEL}\n
        Course will be accessible on ${requestData?.startDate}.`
        modalData.actionLabel = "Okay"
        yield put(fetchCourseById(courseId))
      }
      yield put(showResponseModal(modalData))
    }
  )
}

function* watchBundleEnrollmentSuccess() {
  yield takeLatest(
    COURSE_BUNDLE_ENROLLMENT_SUCCESSFUL,
    function* handleBundleEnrollmentSuccess({ payload }) {
      const { data } = payload

      const courses = yield select((state) => state.coursesReducer)
      const { selectedBundle } = courses

      let modalData = {
        modalType: RESPONSE_MODAL_TYPES.SUCCESS,
        header: data.success,
        message: MODAL_MESSAGES.BUNDLE_ENROLLMENT_SUCCESSFUL,
        actionLabel: "VIEW COURSES",
        verified: true,
      }

      yield put(fetchBundleById(selectedBundle?.id))
      yield put(showResponseModal(modalData))
    }
  )
}

function* watchCourseEnrollmentFailed() {
  yield takeLatest(
    COURSE_ENROLLMENT_FAILED,
    function* handleCourseEnrollmentFailed({ error }) {
      const { status, data } = error.response || {}

      let modalData = {
        modalType: RESPONSE_MODAL_TYPES.ERROR,
      }

      if (status === 400) {
        if (data.detail === "User is already enrolled.") {
          modalData.header = data.detail
          modalData.message = MODAL_MESSAGES.ALREADY_ENROLLED
          modalData.actionLabel = "REFRESH PAGE"
          modalData.action = () => window.location.reload()
          modalData.verified = false
        } else {
          modalData.header = data.detail
          modalData.message = MODAL_MESSAGES.NOT_VERIFIED
          modalData.actionLabel = "RESEND EMAIL"
          modalData.verified = false
        }
      } else {
        modalData.header = "Internal Server Error"
        modalData.message = ERROR_500
        modalData.actionLabel = "GO TO COURSES"
        modalData.action = () => history.push("/courses")
        modalData.verified = false
      }

      yield put(showResponseModal(modalData))
    }
  )
}

function* watchBundleEnrollmentFailed() {
  yield takeLatest(
    COURSE_BUNDLE_ENROLLMENT_FAILED,
    function* handleBundleEnrollmentFailed({ error }) {
      const { status, data } = error.response || {}

      let modalData = {
        modalType: RESPONSE_MODAL_TYPES.ERROR,
      }

      if (status === 400) {
        if (data.detail === "User is already enrolled.") {
          modalData.header = data.detail
          modalData.message = MODAL_MESSAGES.ALREADY_ENROLLED
          modalData.actionLabel = "REFRESH PAGE"
          modalData.action = () => window.location.reload()
          modalData.verified = false
        } else {
          modalData.header = data.detail
          modalData.message = MODAL_MESSAGES.NOT_VERIFIED
          modalData.actionLabel = "RESEND EMAIL"
          modalData.verified = false
        }
      } else {
        modalData.header = "Internal Server Error"
        modalData.message = ERROR_500
        modalData.actionLabel = "GO TO COURSE BUNDLES"
        modalData.action = () => history.push("/course-bundles")
        modalData.verified = false
      }

      yield put(showResponseModal(modalData))
    }
  )
}

function* watchUpdateProfileSuccess() {
  yield takeLatest(UPDATE_PROFILE_SUCCESS, function* handleUpdateProfile() {
    const auth = yield select((state) => state.authReducer)
    const school = yield select((state) => state.schoolReducer)

    let modalData = {
      modalType: RESPONSE_MODAL_TYPES.SUCCESS,
      header: "Profile update successful!",
      message: "Thank you for completing your profile.",
    }

    if (auth.role === "ST" && school.schoolData.config.products.mcle) {
      modalData.actionLabel = "Continue to MCLE Profile"
      modalData.action = () => {
        closeResponseModal()
        history.push(`/student/mcle-profile`)
      }
    } else {
      modalData.actionLabel = "Okay"
      modalData.action = () => closeResponseModal()
    }

    yield put(getUserProfile({ userId: auth.userId, role: auth.role }))
    yield put(showResponseModal(modalData))
  })
}

function* watchGetMCLEProfile() {
  yield takeLatest(
    GET_MCLE_PROFILE_SUCCESS,
    function* handleGetMCLEProfile({ payload }) {
      const { data } = payload
      yield put(setMCLEProfile(data))
    }
  )
}

function* watchUpdateMCLEProfileSuccess() {
  yield takeLatest(
    UPDATE_MCLE_PROFILE_SUCCESS,
    function* handleUpdateMCLEProfile() {
      const auth = yield select((state) => state.authReducer)
      const { userDetails = {} } = yield select((state) => state.userReducer)
      const { schoolData = {} } = yield select((state) => state.schoolReducer)

      const { config: schoolConfig = {} } = schoolData
      const schoolPrimaryId = schoolConfig?.products?.primary_id || false

      let modalData = {
        modalType: RESPONSE_MODAL_TYPES.SUCCESS,
        header: "MCLE profile submitted!",
        message: "Thank you for completing the required information.",
        actionLabel: "Okay",
        action: () => {
          closeResponseModal()
        },
      }

      if (userDetails?.step === "1") {
        modalData.actionLabel = "Go to your profile."
        modalData.action = () => history.push("/student/profile")
      } else if (!isIdVerified(userDetails?.user_ids, schoolPrimaryId)) {
        modalData.actionLabel = "Continue to Authenticate ID"
        modalData.action = () => history.push(`/student/authenticate-id`)
      }
      yield put(getUserProfile({ userId: auth.userId, role: auth.role }))
      yield put(getMCLEProfile({ id: auth.userId }))
      yield put(showResponseModal(modalData))
    }
  )
}

function* watchStartCourseSuccess() {
  yield takeLatest(
    START_COURSE_SUCCESSFUL,
    function* handleStartCourseSuccess({ payload }) {
      const { data } = payload
      const courses = yield select((state) => state.coursesReducer)
      const slug = courses?.selectedCourse?.slug

      yield history.push(`/course/${slug}/module/${data.courseware.id}`)
      yield localStorage.setItem("lms-theme", "")
      yield put(setLMSTheme(""))
    }
  )
}

function* watchStartCourseFailed() {
  yield takeLatest(
    START_COURSE_FAILED,
    function* handleStartCourseFailed({ error }) {
      const { data } = error.response || {}

      toast.error("Failed to start course")

      yield console.log(data)
    }
  )
}

function* watchPatchNoteSuccess() {
  yield takeLatest(PATCH_NOTES_SUCCESSFUL, function handlePatchNoteSuccess() {
    toast.success("Successfully saved notes!")
  })
}

function* watchPatchNoteFailed() {
  yield takeLatest(
    PATCH_NOTES_FAILED,
    function* handlePatchNoteFailed({ error }) {
      const { data } = error.response || {}

      toast.error("Failed to saved notes")

      yield console.log(data)
    }
  )
}

function* watchSaveProgressSuccess() {
  yield takeLatest(
    SET_COURSEWARE_PROGRESS_SUCCESSFUL,
    function* handleSaveProgressSuccess({ payload }) {
      const { data } = payload
      yield console.log(data)
    }
  )
}

function* watchSaveProgressFailed() {
  yield takeLatest(
    SET_COURSEWARE_PROGRESS_FAILED,
    function* handleSaveProgressFailed({ error }) {
      const { data } = error.response || {}
      yield console.log(data)
    }
  )
}

function* watchCompleteCoursewareSuccess() {
  yield takeLatest(
    COMPLETE_COURSEWARE_SUCCESSFUL,
    function* handleCompleteCoursewareSuccess() {
      const user = yield select((state) => state.userReducer)
      const courses = yield select((state) => state.coursesReducer)

      const {
        selectedCourse: { modules = [], id: courseId },
      } = courses

      const path = window.location.pathname

      const COURSEWARE_TYPES = ["exam", "survey"]

      if (user.coursewareDetails.next) {
        if (!COURSEWARE_TYPES.includes(user.coursewareDetails.type)) {
          const nextPath = path.replace(
            user.coursewareDetails.id,
            user.coursewareDetails.next
          )
          yield history.push(nextPath)
        }
      } else {
        let nextCourseware = null

        modules.some((module) => {
          module.coursewares.some((courseware) => {
            if (!courseware.completed) {
              nextCourseware = courseware.id
              return true
            }
            return false
          })

          return nextCourseware !== null
        })

        if (nextCourseware !== null) {
          const nextPath = path.replace(
            user.coursewareDetails.id,
            nextCourseware
          )
          yield history.push(nextPath)
        }
      }

      yield put(fetchCourseById(courseId))
    }
  )
}

function* watchCompleteCoursewareFailed() {
  yield takeLatest(
    COMPLETE_COURSEWARE_FAILED,
    function* handleCompleteCoursewareFailed({ error }) {
      const { data } = error.response || {}
      yield console.log(data)
    }
  )
}

function* watchUpdateProfileFailed() {
  yield takeLatest(
    [UPDATE_PROFILE_FAILED],
    function* handleUpdateProfileFailed({ error }) {
      const { status, data } = error.response
      if (status === 500) {
        yield put(setUserError(ERROR_500))
      } else {
        yield put(setUserError(data))
      }
    }
  )
}

function* watchVerifyKYCSuccess() {
  yield takeLatest(
    UPLOAD_KYC_ID_TEMPLATE_SUCCESSFUL,
    function* handleVerifyKYC({ payload }) {
      const { data } = payload
      const auth = yield select((state) => state.authReducer)
      const school = yield select((state) => state.schoolReducer)
      const { schoolData = {} } = school
      yield put(getUserProfile({ userId: auth.userId, role: auth.role }))
      yield put(
        getWalletTransactions({
          userId: auth.userId,
        })
      )
      yield put(getWalletBalance())
      yield put(getUserNotifications({ page: 1 }))
      if (data.status === "success") {
        let modalData = {
          modalType: RESPONSE_MODAL_TYPES.SUCCESS,
          header: "ID Verification Success",
          message: data.message,
          actionLabel: "Okay",
          action: () => closeResponseModal(),
        }
        if (data?.step === 1) {
          modalData.actionLabel = "Go to your profile."
          modalData.action = () => history.push("/student/profile")
        } else if (data?.info_submitted === false) {
          modalData.actionLabel = "Go to your MCLE profile."
          modalData.action = () => history.push("/student/mcle-profile")
        } else if (
          schoolData?.config?.products?.mcle &&
          data?.info_submitted &&
          data?.step !== 1
        ) {
          modalData.actionLabel = "Go to Course Bundles"
          modalData.action = () => history.push("/course-bundles")
        } else if (data?.step !== 1) {
          modalData.actionLabel = "Go to Courses"
          modalData.action = () => history.push("/courses")
        }
        yield put(showResponseModal(modalData))
      }
    }
  )
}

function* watchManualKYCSuccess() {
  yield takeLatest(
    SUBMIT_MANUAL_KYC_SUCCESSFUL,
    function* handleManualKYC({ payload }) {
      const auth = yield select((state) => state.authReducer)
      const school = yield select((state) => state.schoolReducer)
      const { data } = payload
      const { schoolData = {} } = school
      let modalData = {
        header: "Request Submitted",
        modalType: RESPONSE_MODAL_TYPES.SUCCESS,
        message: data.message,
        actionLabel: "Okay",
        action: () => closeResponseModal(),
      }

      let manualKey = `KYCManual-${auth.userId}`
      yield localStorage.setItem(manualKey, moment().add(3, "days").format())

      let retryKey = `KYCRetry-${auth.userId}`
      yield localStorage.removeItem(retryKey)
      yield put(getUserProfile({ userId: auth.userId, role: auth.role }))
      yield put(
        getWalletTransactions({
          userId: auth.userId,
        })
      )
      yield put(getWalletBalance())
      yield put(getUserNotifications({ page: 1 }))
      if (data?.step === 1) {
        modalData.actionLabel = "Go to your profile."
        modalData.action = () => history.push("/student/profile")
      } else if (data?.info_submitted === false) {
        modalData.actionLabel = "Go to your MCLE profile."
        modalData.action = () => history.push("/student/mcle-profile")
      } else if (
        schoolData?.config?.products?.mcle &&
        data?.info_submitted &&
        data?.step !== 1
      ) {
        modalData.actionLabel = "Go to Course Bundles"
        modalData.action = () => history.push("/course-bundles")
      } else if (data?.step !== 1) {
        modalData.actionLabel = "Go to Courses"
        modalData.action = () => history.push("/courses")
      }
      yield put(showResponseModal(modalData))
    }
  )
}

function* watchGetClassScheduleByIdSuccess() {
  yield takeLatest(
    GET_CLASS_SCHEDULE_SUCCESSFUL,
    function* handleGetClassScheduleByIdSuccess({ payload }) {
      const { data } = payload

      let modalData = {
        modalType: CUSTOM_MODAL_TYPES.SCHEDULE,
        data: data,
        actionLabel: "OK, GOT IT!",
        action: () => closeResponseModal(),
      }

      yield put(showResponseModal(modalData))
    }
  )
}

function* watchGetClassScheduleByIdFailed() {
  yield takeLatest(
    GET_CLASS_SCHEDULE_FAILED,
    function* handleGetClassScheduleByIdFailed({ error }) {
      const { data } = error.response

      yield console.log(data)
    }
  )
}

function* watchStartExamSuccessful() {
  yield takeLatest(
    START_EXAM_SUCCESSFUL,
    function* handleStartExamSuccessful({ payload }) {
      const user = yield select((state) => state.userReducer)

      yield put(getCoursewareById({ id: user.coursewareDetails.id }))
    }
  )
}

function* watchStartExamFailed() {
  yield takeLatest(
    START_EXAM_FAILED,
    function* handleStartExamFailed({ error }) {
      const { data } = error.response

      yield console.log(data)
    }
  )
}

function* watchToggleJoinClass() {
  yield takeLatest(
    TOGGLE_JOIN_CLASS,
    function* handleToggleJoinClass({ payload }) {
      const { confRoomName, useStudios, url } = payload
      if (useStudios && url) {
        yield window.open(
          `${window.location.origin}/studios/${confRoomName}`,
          "_blank"
        )
      } else {
        yield window.open(url, "_blank")
      }
    }
  )
}

function* watchSubmitAnswerSuccessful() {
  yield takeLatest(
    SUBMIT_ANSWER_SUCCESSFUL,
    function* handleSubmitAnswerSuccessful({ payload }) {
      const { data } = payload

      const user = yield select((state) => state.userReducer)
      const courses = yield select((state) => state.coursesReducer)

      const {
        coursewareDetails: { id: coursewareId, type: courewareType },
      } = user
      const {
        selectedCourse: { id: courseId },
      } = courses

      yield put(getCoursewareById({ id: coursewareId }))

      if (data?.date_submitted) {
        if (courewareType === "exam") {
          toast.success("Exam submitted!")
        } else if (courewareType === "survey") {
          toast.success("Survey form submitted!")
        }
        yield put(fetchCourseById(courseId))
      }
    }
  )
}

function* watchSubmitAnswerFailed() {
  yield takeLatest(
    SUBMIT_ANSWER_FAILED,
    function* handleSubmitAnswerFailed({ error }) {
      const { data } = error.response

      yield console.log(data)
    }
  )
}

function* watchUpdateProfilePhotoSuccessful() {
  yield takeLatest(
    UPDATE_PROFILE_PHOTO_SUCCESSFUL,
    function* handleUpdateProfilePhotoSuccessful() {
      const auth = yield select((state) => state.authReducer)

      let modalData = {}
      modalData.header = "Upload Successful!"
      modalData.modalType = RESPONSE_MODAL_TYPES.SUCCESS
      modalData.message = MODAL_MESSAGES.UPLOAD_PROFILE_PHOTO_SUCCESSFUL
      modalData.actionLabel = "CONTINUE"
      modalData.action = () => history.push("/student/profile")
      modalData.verified = false

      yield put(hideProfilePhotoModal())
      yield put(getUserProfile({ userId: auth.userId, role: auth.role }))
      yield put(showResponseModal(modalData))
    }
  )
}

function* watchUpdateProfilePhotoFailed() {
  yield takeLatest(
    UPDATE_PROFILE_PHOTO_FAILED,
    function* handleUpdateProfilePhotoFailed({ error }) {
      const { status, data } = error.response

      if (status === 500) {
        yield put(setUserError(ERROR_500))
      } else {
        const error = isJsonString(data) ? data : "Something went wrong."
        yield put(setUserError(error))
      }
    }
  )
}

function* watchPaidCourseEnrollmentSuccess() {
  yield takeLatest(
    PAID_COURSE_ENROLLMENT_SUCCESSFUL,
    function* handlePaidCourseEnrollmentSuccess({ payload }) {
      const { data } = payload

      let modalData = {
        header: data.success,
        verified: true,
      }

      const courses = yield select((state) => state.coursesReducer)

      if (data?.redirect_url) {
        yield window.location.replace(data?.redirect_url)
      } else if (["notpaid", "partial", "collection"].includes(data?.status)) {
        modalData.modalType = RESPONSE_MODAL_TYPES.WARNING
        modalData.message = MODAL_MESSAGES.PAYMENT_PENDING
        modalData.actionLabel = "VIEW COURSE"
        modalData.action = () =>
          history.push(`/course/${courses?.selectedCourse.slug}`)
        yield put(showResponseModal(modalData))
      } else if (["pay_later"].includes(data?.status)) {
        modalData.modalType = RESPONSE_MODAL_TYPES.WARNING
        modalData.message = MODAL_MESSAGES.PAYMENT_LATER_PENDING
        modalData.actionLabel = "VIEW COURSE"
        modalData.action = () =>
          history.push(`/course/${courses?.selectedCourse.slug}`)
        yield put(showResponseModal(modalData))
      } else {
        modalData.modalType = RESPONSE_MODAL_TYPES.SUCCESS
        modalData.message = MODAL_MESSAGES.PAYMENT_COMPLETE
        modalData.actionLabel = "VIEW COURSE"
        modalData.action = () =>
          history.push(`/course/${courses?.selectedCourse?.slug}`)
        yield put(showResponseModal(modalData))
      }
    }
  )
}

function* watchPaidCourseEnrollmentFailed() {
  yield takeLatest(
    PAID_COURSE_ENROLLMENT_FAILED,
    function* handlePaidCourseEnrollmentFailed({ error }) {
      const { status, data } = error.response || {}

      let modalData = {
        modalType: RESPONSE_MODAL_TYPES.ERROR,
      }

      if (status === 400) {
        if (data.detail === "User is already enrolled.") {
          modalData.header = data.detail
          modalData.message = MODAL_MESSAGES.ALREADY_ENROLLED
          modalData.actionLabel = "REFRESH PAGE"
          modalData.action = () => window.location.reload()
          modalData.verified = false
        } else {
          modalData.header = data.detail
          modalData.message = MODAL_MESSAGES.NOT_VERIFIED
          modalData.actionLabel = "RESEND EMAIL"
          modalData.verified = false
        }
      } else {
        modalData.header = "Payment Failed"
        modalData.message = MODAL_MESSAGES.PAYMENT_FAILED
        modalData.actionLabel = "GO TO COURSES"
        modalData.action = () => history.push("/courses")
        modalData.verified = false
      }

      yield put(showResponseModal(modalData))
    }
  )
}

function* watchPaidBundleEnrollmentSuccess() {
  yield takeLatest(
    PAID_BUNDLE_ENROLLMENT_SUCCESSFUL,
    function* handlePaidBundleEnrollmentSuccess({ payload }) {
      const { data } = payload

      let modalData = {
        header: data.success,
        verified: true,
      }

      const courses = yield select((state) => state.coursesReducer)

      if (data?.redirect_url) {
        yield window.location.replace(data?.redirect_url)
      } else if (["notpaid", "partial", "collection"].includes(data?.status)) {
        modalData.modalType = RESPONSE_MODAL_TYPES.WARNING
        modalData.message = MODAL_MESSAGES.BUNDLE_PAYMENT_PENDING
        modalData.actionLabel = "GO TO DASHBOARD"
        modalData.action = () => history.push("/")
        yield put(showResponseModal(modalData))
      } else {
        modalData.modalType = RESPONSE_MODAL_TYPES.SUCCESS
        modalData.message = MODAL_MESSAGES.BUNDLE_PAYMENT_COMPLETE
        modalData.actionLabel = "GO TO THE BUNDLE"
        modalData.action = () =>
          history.push(`/course-bundle/${courses?.selectedBundle?.slug}`)
        yield put(showResponseModal(modalData))
      }
    }
  )
}

function* watchPaidBundleEnrollmentFailed() {
  yield takeLatest(
    PAID_BUNDLE_ENROLLMENT_FAILED,
    function* handlePaidBundleEnrollmentFailed({ error }) {
      const { status, data } = error.response || {}

      let modalData = {
        modalType: RESPONSE_MODAL_TYPES.ERROR,
      }

      if (status === 400) {
        if (data.detail === "User is already enrolled.") {
          modalData.header = data.detail
          modalData.message = MODAL_MESSAGES.ALREADY_ENROLLED
          modalData.actionLabel = "REFRESH PAGE"
          modalData.action = () => window.location.reload()
          modalData.verified = false
        } else {
          modalData.header = data.detail
          modalData.message = MODAL_MESSAGES.NOT_VERIFIED
          modalData.actionLabel = "RESEND EMAIL"
          modalData.verified = false
        }
      } else {
        modalData.header = "Payment Failed"
        modalData.message = MODAL_MESSAGES.PAYMENT_FAILED
        modalData.actionLabel = "GO TO COURSES"
        modalData.action = () => history.push("/course-bundles")
        modalData.verified = false
      }

      yield put(showResponseModal(modalData))
    }
  )
}

function* watchVerifyWalletAddressSuccess() {
  yield takeLatest(
    VERIFY_WALLET_ADDRESS_SUCCESSFUL,
    function* handleVerifyWalletAddressSuccess({ payload }) {
      const { data } = payload

      yield put(getWalletBalance())

      if (!data.success) {
        toast.error("Wallet verification failed")

        yield put(setVerifyingAddress(false))
        yield put(setWalletConnected(false))
      }
    }
  )
}

function* watchVerifyWalletAddressFailed() {
  yield takeLatest(
    VERIFY_WALLET_ADDRESS_FAILED,
    function* handleVerifyWalletAddressFailed({ error }) {
      // const { status, data } = error.response

      yield put(setVerifyingAddress(false))
      yield put(setWalletConnected(false))
    }
  )
}

function* watchGetWalletNonceSuccess() {
  yield takeLatest(
    GET_USER_WALLET_NONCE_SUCCESSFUL,
    function* handleGetWalletNonceSuccess({ payload }) {
      const { data } = payload

      if (!data.nonce && !data.success) {
        toast.error("User account not found")
      }

      yield put(setVerifyingAddress(false))
    }
  )
}

function* watchGetWalletNonceFailed() {
  yield takeLatest(
    GET_USER_WALLET_NONCE_FAILED,
    function* handleGetWalletNonceFailed() {
      toast.error("Failed to get wallet details")

      yield put(setVerifyingAddress(false))
    }
  )
}

export default [
  watchGetUserNotifications,
  watchGetUserProfileSuccess,
  watchGetUserProfileFailed,
  watchCourseEnrollmentSuccess,
  watchCourseEnrollmentFailed,
  watchUpdateProfileSuccess,
  watchStartCourseSuccess,
  watchStartCourseFailed,
  watchPatchNoteSuccess,
  watchPatchNoteFailed,
  watchSaveProgressSuccess,
  watchSaveProgressFailed,
  watchCompleteCoursewareSuccess,
  watchCompleteCoursewareFailed,
  watchUpdateProfileFailed,
  watchVerifyKYCSuccess,
  watchGetClassScheduleByIdSuccess,
  watchGetClassScheduleByIdFailed,
  watchStartExamSuccessful,
  watchStartExamFailed,
  watchToggleJoinClass,
  watchSubmitAnswerSuccessful,
  watchSubmitAnswerFailed,
  watchPaidCourseEnrollmentSuccess,
  watchPaidCourseEnrollmentFailed,
  watchPaidBundleEnrollmentSuccess,
  watchPaidBundleEnrollmentFailed,
  watchGetMCLEProfile,
  watchUpdateMCLEProfileSuccess,
  watchManualKYCSuccess,
  watchUpdateProfilePhotoSuccessful,
  watchUpdateProfilePhotoFailed,
  watchBundleEnrollmentSuccess,
  watchBundleEnrollmentFailed,
  watchVerifyWalletAddressSuccess,
  watchVerifyWalletAddressFailed,
  watchGetWalletNonceSuccess,
  watchGetWalletNonceFailed,
]
